import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const pageTitles: Record<string, { title: string; subtitle: string }> = {
  '/dashboard':  { title: 'Dashboard',     subtitle: 'Welcome back! Here\'s your progress.' },
  '/vocabulary': { title: 'My Vocabulary', subtitle: 'Manage and explore your word list.' },
  '/learn':      { title: 'Learn',         subtitle: 'Start a new learning session.' },
  '/review':     { title: 'Review',        subtitle: 'Reinforce words with spaced repetition.' },
  '/statistics': { title: 'Statistics',    subtitle: 'Track your learning progress over time.' },
  '/settings':   { title: 'Settings',      subtitle: 'Manage your account and preferences.' },
  '/admin':      { title: 'Admin',         subtitle: 'Manage user accounts and access.' },
};

const defaultPage = { title: 'IELTS Vocabulary', subtitle: 'Learning Platform' };

interface TopBarProps {
  onToggleMobileNav?: () => void;
}

export function TopBar({ onToggleMobileNav }: TopBarProps): React.ReactElement {
  const location = useLocation();
  const { user, logout } = useAuth();
  const page = pageTitles[location.pathname] ?? defaultPage;

  const displayName = user?.name || user?.email?.split('@')[0] || 'User';
  const initial = (user?.name?.[0] || user?.email?.[0] || 'U').toUpperCase();

  return (
    <header className="flex items-center justify-between px-4 sm:px-6 h-16 border-b border-white/8 bg-surface-900/60 backdrop-blur-sm shrink-0">
      {/* Page title & Mobile Menu Toggle */}
      <div className="flex items-center gap-3">
        {onToggleMobileNav && (
          <button
            type="button"
            id="mobile-nav-toggle-btn"
            onClick={onToggleMobileNav}
            aria-label="Toggle navigation menu"
            className="md:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 border border-white/8 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
        )}
        <div>
          <h1 className="text-base sm:text-lg font-semibold text-white leading-tight">{page.title}</h1>
          <p className="text-xs text-slate-500 leading-tight hidden xs:block sm:block">{page.subtitle}</p>
        </div>
      </div>

      {/* Right side — user info & actions */}
      <div className="flex items-center gap-4">
        {/* Status badge */}
        <div className="hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs font-medium text-emerald-400">Online</span>
        </div>

        {/* User profile & logout */}
        {user && (
          <div className="flex items-center gap-3 pl-2 border-l border-white/8">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center shadow-md font-semibold text-white text-xs">
                {initial}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-xs font-medium text-white leading-tight truncate max-w-[120px]">
                  {displayName}
                </p>
                <p className="text-[10px] text-slate-400 leading-tight truncate max-w-[120px]">
                  {user.email}
                </p>
              </div>
            </div>

            <button
              id="topbar-logout-btn"
              onClick={() => void logout()}
              title="Sign out"
              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 border border-transparent hover:border-rose-500/20 transition-all text-xs flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
              </svg>
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
